// Inline composer for replying to a thread (repair or tenancy anchored).
// Calls compose-repair-message edge fn which sets the right Reply-To and
// emails every other party on the conversation.

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getSupabase } from "@/lib/supabase";
import { Send } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Anchor } from "./MessageThread";

// Mirrors the server-side cap in compose-repair-message edge fn so we fail
// fast in the client rather than round-tripping to a 400.
const MAX_BODY_CHARS = 8000;

export function MessageComposer({
  anchor,
  title = "Reply",
  onSent,
}: {
  anchor: Anchor;
  title?: string;
  onSent?: () => void;
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentInfo, setSentInfo] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) {
      setError("Type a message first.");
      return;
    }
    if (trimmed.length > MAX_BODY_CHARS) {
      setError(`Message is too long. Keep it under ${MAX_BODY_CHARS.toLocaleString()} characters.`);
      return;
    }
    setSending(true);
    setError(null);
    setSentInfo(null);
    try {
      const sb = getSupabase();
      const payload =
        anchor.kind === "repair"
          ? { repair_id: anchor.id, body_text: trimmed }
          : { tenancy_id: anchor.id, body_text: trimmed };
      const { data, error: invokeErr } = await sb.functions.invoke<{
        ok: boolean;
        recipients?: string[];
        error?: string;
      }>("compose-repair-message", { body: payload });
      if (invokeErr) {
        setError(invokeErr.message);
        return;
      }
      if (data?.error) {
        setError(data.error);
        return;
      }
      setText("");
      const n = data?.recipients?.length ?? 0;
      setSentInfo(n > 0 ? `Sent to ${n} recipient${n === 1 ? "" : "s"}.` : "Logged on the thread.");
      onSent?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your reply…"
            rows={4}
            disabled={sending}
            maxLength={MAX_BODY_CHARS}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                onSubmit(e as unknown as FormEvent);
              }
            }}
            className="w-full resize-none bg-bg-card border border-line rounded-xl px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-dim/60 focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-1 focus-visible:ring-accent/40"
          />
          {error ? (
            <p role="alert" className="text-xs text-red-300">
              {error}
            </p>
          ) : null}
          {sentInfo ? <p className="text-xs text-ink-dim">{sentInfo}</p> : null}
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-ink-dim">
              Cmd/Ctrl + Enter to send.
              {text.length > MAX_BODY_CHARS * 0.9 ? (
                <span
                  className={`ml-2 ${text.length >= MAX_BODY_CHARS ? "text-red-300" : "text-ink-dim"}`}
                >
                  {(MAX_BODY_CHARS - text.length).toLocaleString()} characters left
                </span>
              ) : null}
            </span>
            <Button type="submit" disabled={sending || text.trim().length === 0}>
              <Send size={14} /> {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
